import { useState, useMemo } from 'react';
import { useProject } from '../../context/useProject';
import { exportCableSchedulePDF, exportCableScheduleCSV } from '../../utils/cableSchedule';
import './ProjectPanel.css';

interface ProjectPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'projects' | 'schedule';

export function ProjectPanel({ isOpen, onClose }: ProjectPanelProps) {
  const {
    currentProject,
    projects,
    createProject,
    openProject,
    closeProject,
    updateProject,
    deleteProject,
    removeFromSchedule,
    updateScheduleEntry,
    clearSchedule,
  } = useProject();

  const [activeTab, setActiveTab] = useState<Tab>('projects');
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Sort projects by update date
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [projects]);

  const handleCreateProject = () => {
    if (newProjectName.trim()) {
      createProject(newProjectName.trim());
      setNewProjectName('');
      setIsCreating(false);
    }
  };

  const handleExportPDF = () => {
    if (!currentProject) return;

    exportCableSchedulePDF({
      projectName: currentProject.name,
      preparedFor: currentProject.client,
      preparedBy: currentProject.preparedBy,
      entries: currentProject.cableSchedule,
    });
  };

  const handleExportCSV = () => {
    if (!currentProject) return;

    const csv = exportCableScheduleCSV(currentProject.cableSchedule);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentProject.name.replace(/\s+/g, '_')}_cable_schedule.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const startEditEntry = (id: string, currentName: string) => {
    setEditingId(id);
    setEditValue(currentName);
  };

  const saveEditEntry = () => {
    if (editingId && editValue.trim()) {
      updateScheduleEntry(editingId, { circuitName: editValue.trim() });
    }
    setEditingId(null);
    setEditValue('');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <aside className="project-panel panel">
        <div className="panel-header">
          <h2>Project Manager</h2>
          <button className="panel-close" onClick={onClose} aria-label="Close panel">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="panel-tabs">
          <button
            className={`panel-tab ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            Projects
          </button>
          <button
            className={`panel-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
            disabled={!currentProject}
          >
            Cable Schedule
            {currentProject && currentProject.cableSchedule.length > 0 && (
              <span className="tab-badge">{currentProject.cableSchedule.length}</span>
            )}
          </button>
        </div>

        <div className="panel-content">
          {activeTab === 'projects' && (
            <div className="projects-tab">
              {/* Current Project Banner */}
              {currentProject && (
                <div className="current-project-banner">
                  <div className="current-project-info">
                    <span className="current-label">Active Project</span>
                    <span className="current-name">{currentProject.name}</span>
                  </div>
                  <button
                    className="btn-close-project"
                    onClick={closeProject}
                    title="Close project"
                  >
                    Close
                  </button>
                </div>
              )}

              {/* Create New Project */}
              {isCreating ? (
                <div className="create-project-form">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Project name..."
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateProject();
                      if (e.key === 'Escape') setIsCreating(false);
                    }}
                  />
                  <div className="form-actions">
                    <button className="btn-cancel" onClick={() => setIsCreating(false)}>
                      Cancel
                    </button>
                    <button className="btn-create" onClick={handleCreateProject}>
                      Create
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn-new-project" onClick={() => setIsCreating(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  New Project
                </button>
              )}

              {/* Projects List */}
              <div className="projects-list">
                {sortedProjects.length === 0 ? (
                  <div className="empty-projects">
                    <p>No projects yet</p>
                    <p className="hint">Create a project to start building a cable schedule</p>
                  </div>
                ) : (
                  sortedProjects.map(project => (
                    <div
                      key={project.id}
                      className={`project-item ${currentProject?.id === project.id ? 'active' : ''}`}
                    >
                      <div className="project-info" onClick={() => openProject(project.id)}>
                        <span className="project-name">{project.name}</span>
                        <span className="project-meta">
                          {project.cableSchedule.length} circuits
                          {' · '}
                          {new Date(project.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        className="btn-delete-project"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Delete project "${project.name}"?`)) {
                            deleteProject(project.id);
                          }
                        }}
                        title="Delete project"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Project Details */}
              {currentProject && (
                <div className="project-details">
                  <h4>Project Details</h4>
                  <div className="detail-field">
                    <label>Client/Customer</label>
                    <input
                      type="text"
                      value={currentProject.client || ''}
                      onChange={(e) => updateProject({ client: e.target.value })}
                      placeholder="Client name..."
                    />
                  </div>
                  <div className="detail-field">
                    <label>Prepared By</label>
                    <input
                      type="text"
                      value={currentProject.preparedBy || ''}
                      onChange={(e) => updateProject({ preparedBy: e.target.value })}
                      placeholder="Your name..."
                    />
                  </div>
                  <div className="detail-field">
                    <label>Description</label>
                    <textarea
                      value={currentProject.description || ''}
                      onChange={(e) => updateProject({ description: e.target.value })}
                      placeholder="Project description..."
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'schedule' && currentProject && (
            <div className="schedule-tab">
              {/* Export Actions */}
              <div className="schedule-actions">
                <button
                  className="btn-export"
                  onClick={handleExportPDF}
                  disabled={currentProject.cableSchedule.length === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  Export PDF
                </button>
                <button
                  className="btn-export secondary"
                  onClick={handleExportCSV}
                  disabled={currentProject.cableSchedule.length === 0}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export CSV
                </button>
                {currentProject.cableSchedule.length > 0 && (
                  <button
                    className="btn-clear"
                    onClick={() => {
                      if (confirm('Clear all entries from the cable schedule?')) {
                        clearSchedule();
                      }
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Schedule Entries */}
              <div className="schedule-list">
                {currentProject.cableSchedule.length === 0 ? (
                  <div className="empty-schedule">
                    <p>No circuits in schedule</p>
                    <p className="hint">
                      Add circuits from the History panel or run calculations to populate the schedule
                    </p>
                  </div>
                ) : (
                  currentProject.cableSchedule.map((entry, index) => (
                    <div key={entry.id} className="schedule-entry">
                      <div className="entry-header">
                        <span className="entry-number">{index + 1}</span>
                        {editingId === entry.id ? (
                          <input
                            type="text"
                            className="entry-name-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={saveEditEntry}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEditEntry();
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            className="entry-name"
                            onClick={() => startEditEntry(entry.id, entry.circuitName)}
                          >
                            {entry.circuitName}
                          </span>
                        )}
                        <button
                          className="btn-remove-entry"
                          onClick={() => removeFromSchedule(entry.id)}
                          title="Remove from schedule"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                      <div className="entry-details">
                        <span>{entry.voltage} V {entry.phase}</span>
                        <span>{entry.wireSize} {entry.material === 'copper' ? 'Cu' : 'Al'}</span>
                        <span>{entry.loadAmps.toFixed(1)} A</span>
                        {entry.lengthFeet && <span>{entry.lengthFeet}ft</span>}
                        {entry.voltageDrop && <span>{entry.voltageDrop.toFixed(2)}% VD</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default ProjectPanel;
